import { Stack } from "@fluentui/react";
import { forwardRef, useImperativeHandle } from "react";
import { logEvent } from "../../helper/AppInsightsHelper";
import { PromptPlaygroundProps } from "../../interfaces/slices.interfaces";
import { openQueryInADX } from "../../Utils/adx-utils";
import { copyFormattedToClipboard } from "../../Utils/js-common";
import { AI_PLAYGROUND_CONSTANTS } from "./AIPlayground.constants";
import { SectionDivider, SharedContentBanner, ShareToast } from "./Components/Common";
import { SaveSessionDialog, ScheduleDialog } from "./Components/Dialogs";
import { ActionButtons, PromptEditor, QueryEditor, ResultsPanel } from "./Components/PromptPlayground";
import { usePromptPlayground } from "./Hooks";
import { SaveSessionDialogInput } from "./Interfaces";
import { playgroundClassNames as classNames } from "./Styles";
import { generateDocx } from "./Utils/documentExport";

const copyToClipboard = (text: string) => {
  if (!text.trim()) return alert("Nothing to copy");
  copyFormattedToClipboard(text).then(() => {
    alert("Copied to clipboard");
  });
};

const PromptPlayground = forwardRef<{ runPrompt: () => void }, PromptPlaygroundProps>(({ selectedPrompt }, ref) => {
  const {
    state,
    updateField,
    executePrompt,
    mappedData,
    markdownRef,
    isExecuting,
    isCreatingShareLink,
    saveSession,
    handleDialogInputChange,
    handleShare,
    shareToastMessage,
    shareToastType,
    isSharedContent,
    dismissShareToast,
    dismissSharedBanner,
    fieldFocused,
    setFieldFocused
  } = usePromptPlayground(selectedPrompt);

  const handleSaveSession = async () => {
    logEvent("Save Session button clicked to save session", "AI Hub - KQL Mode", "PromptPlayground");
    const sessionData: SaveSessionDialogInput = state.dialogInputData;

    try {
      updateField("isSaving", true);
      await saveSession(sessionData).unwrap();
      updateField("isSaving", false);
      updateField("isSaved", true);
      updateField("sessionName", "");
      setTimeout(() => {
        updateField("isSaveDialogOpen", false);
        updateField("isSaved", false);
      }, 3000);
    } catch (error) {
      updateField("isSaving", false);
      alert(AI_PLAYGROUND_CONSTANTS.MESSAGES.SAVE_SESSION_ERROR);
      console.error(error);
    }
  };

  const handleDownload = async () => {
    logEvent("Download Document in Prompt Playground", "AI Hub - KQL Mode", "PromptPlayground", [
      { query: state.query, prompt: state.prompt }
    ]);
    await generateDocx(state.query, state.prompt, state.results, state.resultImages, markdownRef);
  };

  useImperativeHandle(ref, () => ({
    runPrompt: executePrompt
  }));

  return (
    <div className={classNames.mainContainerStyle}>
      <SectionDivider text={"Prompt Playground"} />
      <SharedContentBanner isSharedContent={isSharedContent} onDismiss={dismissSharedBanner} />
      <ShareToast message={shareToastMessage} type={shareToastType} onDismiss={dismissShareToast} />
      <div className={classNames.gridContainer}>
        <Stack className={classNames.leftContainerStyle} tokens={{ childrenGap: 24 }}>
          <QueryEditor
            query={state.query}
            onChange={(value) => updateField("query", value)}
            onCopy={copyToClipboard}
            onOpenInADX={openQueryInADX}
          />
          <PromptEditor
            prompt={state.prompt}
            onChange={(value) => updateField("prompt", value)}
            onCopy={copyToClipboard}
          />
        </Stack>
        <ResultsPanel
          results={state.results}
          resultImages={state.resultImages}
          isExecuting={isExecuting}
          onCopy={copyToClipboard}
          markdownRef={markdownRef}
        />
        <ActionButtons
          onExecute={executePrompt}
          onSaveSession={() => updateField("isSaveDialogOpen", true)}
          onSchedule={() => updateField("isScheduleDialogOpen", true)}
          onDownload={handleDownload}
          onShare={handleShare}
          isExecuting={isExecuting}
          isSharing={isCreatingShareLink}
          hasResults={!!state.results}
          disabled={!state.query.trim() || !state.prompt.trim() || isExecuting || !state.results}
        />
      </div>

      <SaveSessionDialog
        isOpen={state.isSaveDialogOpen || false}
        onClose={() => updateField("isSaveDialogOpen", false)}
        onDialogInputChange={(name: keyof SaveSessionDialogInput, value: string | boolean) => {
          handleDialogInputChange(name, value);
        }}
        onSave={handleSaveSession}
        isSaving={state.isSaving || false}
        isSaved={state.isSaved || false}
        inputData={state.dialogInputData}
        fieldsFocused={fieldFocused}
        setFieldsFocused={setFieldFocused}
      />

      <ScheduleDialog
        isOpen={state.isScheduleDialogOpen || false}
        onClose={() => updateField("isScheduleDialogOpen", false)}
        schedules={state.schedules}
        onSchedulesChange={(schedules) => updateField("schedules", schedules)}
        onSave={() => { }}
        isSaving={state.isSavingSchedules || false}
        isSaved={state.isSchedulesSaved || false}
        sessionNameOptions={mappedData.map((item) => ({
          key: `${item.sessionType} - ${item.sessionName}`,
          text: `${item.sessionType === "user" ? "👤" : "🤖"} ${item.sessionName}`
        }))}
      />
    </div>
  );
});

PromptPlayground.displayName = "PromptPlayground";

export default PromptPlayground;
